import { getAnalytics } from "@tm/insurance/analytics";
import { useBreakpointV2 } from "@tm/ui-widgets";
import React, { useState } from "react";
import EducationModal from "../EducationModal/EducationModal";
import FormattedText from "../FormattedText/FormattedText";
import ReactPortalComp from "../ReactPortalComp";
import {
  ArrowButton,
  ArrowIcon,
  ArrowPath,
  Backdrop,
  BannerWrapper,
  Content,
  DesktopModal,
  DesktopModalContainer,
  Icon,
  KnowMoreTag,
  MobileModalStyle,
  Title,
} from "./ProductInfoSection.style";

const FullscreenMobileModal = ({
  open,
  onClose,
  title,
  url,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  url?: string;
}) => {
  if (!open) return null;
  return (
    <MobileModalStyle>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px",
          background: "#fff",
          borderBottom: "1px solid #eee",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 16 }}>{title}</span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            fontSize: 24,
            cursor: "pointer",
          }}
        >
          &times;
        </button>
      </div>
      <iframe
        src={url}
        style={{ flex: 1, width: "100%", border: "none", background: "#fff" }}
        title={title || "Know More"}
        allowFullScreen
      />
    </MobileModalStyle>
  );
};

interface ProductInfoSectionProps {
  productInfoBanner: any;
  onEducationModalShown?: () => void;
  onEducationModalNextClick?: () => void;
  onEducationModalSkipClick?: () => void;
  onEducationModalPageShown?: (pageId: string) => void;
  onKnowMoreClick?: () => void;
  onProductCardClick?: () => void;
}

const ProductInfoSection = ({
  productInfoBanner,
  onEducationModalShown,
  onEducationModalNextClick,
  onEducationModalSkipClick,
  onEducationModalPageShown,
  onKnowMoreClick,
  onProductCardClick,
}: ProductInfoSectionProps) => {
  const device = useBreakpointV2();
  const [showKnowMoreModal, setShowKnowMoreModal] = useState(false);
  const [showEducationModal, setShowEducationModal] = useState(false);
  const analytics = getAnalytics();

  const handleKnowMoreClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    setShowKnowMoreModal(true);
    onKnowMoreClick && onKnowMoreClick();
    e.stopPropagation();
  };

  const handleClose = () => setShowKnowMoreModal(false);

  const handleEducationModalSkip = () => {
    setShowEducationModal(false);
  };

  const handleProductCardClick = () => {
    setShowEducationModal(true);
    onProductCardClick && onProductCardClick();
  };

  const isMobile = device === "sm";

  const RightArrow = ({ color = "#FFFFFF" }) => (
    <ArrowIcon
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <ArrowPath
        d="M14.577 10.665c0-.167-.033-.32-.097-.46a1.414 1.414 0 0 0-.291-.405L7.974 3.714a1.059 1.059 0 0 0-.768-.307 1.06 1.06 0 0 0-.542.146 1.138 1.138 0 0 0-.404.396 1.032 1.032 0 0 0-.145.541c0 .302.113.566.34.792l5.536 5.383-5.537 5.382a1.103 1.103 0 0 0-.194 1.342c.103.162.237.291.405.388.167.097.347.145.541.145.302 0 .558-.102.768-.307l6.215-6.085c.134-.13.231-.264.29-.404.065-.146.098-.3.098-.461Z"
        fill={color}
      />
    </ArrowIcon>
  );

  return (
    <>
      <BannerWrapper onClick={handleProductCardClick}>
        <Icon src={productInfoBanner.icon} alt="Product Icon" />
        <Content>
          <Title>
            <FormattedText value={productInfoBanner?.title} />
          </Title>
          {productInfoBanner?.knowMore?.title && (
            <KnowMoreTag isMobile={isMobile} onClick={handleKnowMoreClick}>
              <FormattedText value={productInfoBanner?.knowMore?.title} />
            </KnowMoreTag>
          )}
        </Content>
        <ArrowButton>
          <RightArrow />
        </ArrowButton>
      </BannerWrapper>
      {showKnowMoreModal && (
        <ReactPortalComp>
          {isMobile ? (
            <FullscreenMobileModal
              open={showKnowMoreModal}
              onClose={handleClose}
              title={
                typeof productInfoBanner?.knowMore?.title === "string"
                  ? productInfoBanner?.knowMore?.title
                  : ""
              }
              url={productInfoBanner?.knowMore?.url}
            />
          ) : (
            <DesktopModalContainer>
              <Backdrop onClick={handleClose} />
              <DesktopModal>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: 16,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 18 }}>
                    {typeof productInfoBanner?.knowMore?.title === "string"
                      ? productInfoBanner?.knowMore?.title
                      : ""}
                  </span>
                  <button
                    onClick={handleClose}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: 24,
                      cursor: "pointer",
                    }}
                  >
                    &times;
                  </button>
                </div>
                <iframe
                  src={productInfoBanner?.knowMore?.url}
                  style={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    border: "none",
                    borderRadius: "0 0 12px 12px",
                    background: "#fff",
                  }}
                  title={
                    typeof productInfoBanner?.knowMore?.title === "string"
                      ? productInfoBanner?.knowMore?.title
                      : ""
                  }
                  allowFullScreen
                />
              </DesktopModal>
            </DesktopModalContainer>
          )}
        </ReactPortalComp>
      )}
      {showEducationModal && productInfoBanner && (
        <EducationModal
          pagesData={productInfoBanner.pages}
          knowMore={productInfoBanner.knowMore}
          setShowEducationModal={setShowEducationModal}
          skipButtonHandler={handleEducationModalSkip}
          onModalShown={onEducationModalShown}
          onNextClick={onEducationModalNextClick}
          onSkipClick={onEducationModalSkipClick}
          onPageShown={onEducationModalPageShown}
        />
      )}
    </>
  );
};

export default ProductInfoSection;
